import {
  CARD_FIELD_OPTIONS,
  CARD_FIELDS,
  CardContents,
  CardFieldKey,
  CardFieldKeys,
  Shop,
} from "@/types/v2";
import { getCardContents } from "@/lib/cards";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/Base/Form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { z, ZodObject, ZodRawShape } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/Base/Button";
import { XIcon } from "lucide-react";
import { Tooltip } from "@/components/Common/Tooltip";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { Switch } from "@/components/Base/Switch";
import { Input } from "@/components/Base/Input";
import { useDebounceCallback } from "@/hooks/useDebounceCallback";

type CardFieldsProps = {
  shop: Shop;
  onSubmit?: (values: FormSchema) => void;
};

const shape: ZodRawShape = {};
CARD_FIELDS.forEach((field) => {
  shape[field.key] = z.string().optional();
  shape[`${field.key}Label`] = z.string().nullable().optional();
});
const formSchema: ZodObject<typeof shape> = z.object({
  ...shape,
  waiterIconEnabled: z.boolean().optional(),
});

const defaultValues: Partial<CardContents> = {
  waiterIconEnabled: false,
};

CARD_FIELDS.forEach((field) => {
  defaultValues[field.key] = "";
});

type FormSchema = Required<
  Pick<
    CardContents,
    | "primaryField"
    | "secondaryField"
    | "tertiaryField"
    | "primaryFieldLabel"
    | "secondaryFieldLabel"
    | "tertiaryFieldLabel"
    | "waiterIconEnabled"
  >
>;
export const CardFields = ({ shop, onSubmit }: CardFieldsProps) => {
  const { terminolizeAuto } = useTerminologyReplacer();
  const cardContents = getCardContents(shop);
  const formDefaultValues: FormSchema = {
    primaryField: cardContents.primaryField ?? "",
    secondaryField: cardContents.secondaryField ?? "",
    tertiaryField: cardContents.tertiaryField ?? "",
    waiterIconEnabled: cardContents.waiterIconEnabled ?? false,
    primaryFieldLabel: cardContents.primaryFieldLabel ?? null,
    secondaryFieldLabel: cardContents.secondaryFieldLabel ?? null,
    tertiaryFieldLabel: cardContents.tertiaryFieldLabel ?? null,
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: formDefaultValues,
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    if (onSubmit) {
      // Convert undefined label values to null for Firestore compatibility
      const sanitizedValues = {
        ...values,
        primaryFieldLabel: values.primaryFieldLabel ?? null,
        secondaryFieldLabel: values.secondaryFieldLabel ?? null,
        tertiaryFieldLabel: values.tertiaryFieldLabel ?? null,
      };
      onSubmit(sanitizedValues as FormSchema);
    }
  };

  const handleSelectChange = (
    fieldValue: CardFieldKey | "",
    fieldName: keyof FormSchema
  ) => {
    form.setValue(fieldName, fieldValue);
    handleSubmit(form.getValues());
  };

  const handleSwitchChange = (
    fieldValue: boolean,
    fieldName: keyof FormSchema
  ) => {
    form.setValue(fieldName, fieldValue);
    handleSubmit(form.getValues());
  };

  const debouncedSubmit = useDebounceCallback(() => {
    handleSubmit(form.getValues());
  }, 500);

  const handleLabelChange = (
    fieldValue: string,
    fieldName: `${CardFieldKeys}Label`
  ) => {
    form.setValue(fieldName, fieldValue);
    debouncedSubmit();
  };

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
        <h1>Card Fields</h1>
        <div className="flex flex-col gap-4 md:grid md:grid-cols-2">
          <div className="flex flex-col gap-4">
            {CARD_FIELDS.map((fieldName) => {
              const fieldValue = form.watch(fieldName.key);
              const isCustom = fieldValue === "Custom";
              return (
                <div key={fieldName.key} className="space-y-2">
                  <FormField
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{fieldName.label}</FormLabel>
                        <FormControl>
                          <div className={"flex flex-row gap-4"}>
                            <Select
                              onValueChange={(value) =>
                                handleSelectChange(
                                  value as CardFieldKey | "",
                                  field.name as keyof FormSchema
                                )
                              }
                              value={field.value}
                            >
                              <SelectTrigger className={"w-48"}>
                                <SelectValue
                                  placeholder={`Choose a ${fieldName.label.toLowerCase()}`}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {CARD_FIELD_OPTIONS.map((name) => (
                                  <SelectItem key={name} value={`${name}`}>
                                    {terminolizeAuto(name)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {field.value !== "" ? (
                              <Tooltip message={"Clear Field Value"}>
                                <Button
                                  type="button"
                                  onClick={() =>
                                    handleSelectChange(
                                      "",
                                      field.name as keyof FormSchema
                                    )
                                  }
                                  variant={"accent"}
                                >
                                  <XIcon />
                                </Button>
                              </Tooltip>
                            ) : null}
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                    name={fieldName.key}
                  />
                  {isCustom && (
                    <FormField
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Custom Label</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder={`Enter custom label for ${fieldName.label.toLowerCase()}`}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                handleLabelChange(
                                  e.target.value,
                                  `${fieldName.key}Label`
                                );
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                      name={`${fieldName.key}Label`}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex flex-col gap-4">
            <FormField
              name={"waiterIconEnabled"}
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Enable Waiter Icon
                    </FormLabel>
                    <FormDescription>
                      Enables the waiter icon in the card header
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(value) =>
                        handleSwitchChange(
                          value,
                          field.name as keyof FormSchema
                        )
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </form>
    </Form>
  );
};
